// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { parseLocalDate } from '@/utils/formatters';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  CheckCircle2, 
  Clock, 
  AlertTriangle, 
  Calendar,
  DollarSign,
  Users,
  Globe,
  Settings,
  GraduationCap,
  TestTube,
  Rocket,
  Trophy,
  BarChart3,
  FileText
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface WhiteLabelImplementation {
  id: string;
  prospect_id: string;
  contract_signed_date?: string;
  implementation_start_date?: string;
  target_go_live_date?: string;
  actual_go_live_date?: string;
  implementation_status: string;
  custom_domain?: string;
  branding_package: string;
  monthly_fee?: number;
  setup_fee?: number;
  contract_length_months: number;
  renewal_date?: string;
  implementation_notes?: string;
  milestones?: any[];
  staff_training_status: string;
  branding_assets_status: string;
  technical_requirements?: any;
  go_live_checklist?: any[];
  success_metrics?: any;
  created_at: string;
  white_label_prospects?: {
    organization_name: string;
    contact_name: string;
    contact_email: string;
    organization_type: string;
  };
}

interface WhiteLabelImplementationTrackerProps {
  searchTerm: string;
}

const implementationStages = [
  { id: 'planning', name: 'Planning', icon: FileText, color: 'bg-blue-100' },
  { id: 'technical_setup', name: 'Technical Setup', icon: Settings, color: 'bg-purple-100' },
  { id: 'branding', name: 'Branding', icon: BarChart3, color: 'bg-orange-100' },
  { id: 'training', name: 'Training', icon: GraduationCap, color: 'bg-yellow-100' },
  { id: 'testing', name: 'Testing', icon: TestTube, color: 'bg-green-100' },
  { id: 'live', name: 'Live', icon: Rocket, color: 'bg-emerald-100' },
  { id: 'complete', name: 'Complete', icon: Trophy, color: 'bg-indigo-100' }
];

const WhiteLabelImplementationTracker: React.FC<WhiteLabelImplementationTrackerProps> = ({ searchTerm }) => {
  const [implementations, setImplementations] = useState<WhiteLabelImplementation[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');

  useEffect(() => {
    fetchImplementations();
  }, []);

  const fetchImplementations = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('white_label_implementations')
        .select(`
          *,
          white_label_prospects (
            organization_name,
            contact_name,
            contact_email,
            organization_type
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setImplementations(data || []);
    } catch (error) {
      console.error('Error fetching implementations:', error);
      toast.error('Failed to load implementations');
    } finally {
      setLoading(false);
    }
  };

  const filteredImplementations = implementations.filter(impl => {
    const matchesSearch = 
      impl.white_label_prospects?.organization_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      impl.white_label_prospects?.contact_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      impl.white_label_prospects?.contact_email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    switch (activeTab) {
      case 'active':
        return matchesSearch && !['complete'].includes(impl.implementation_status);
      case 'completed':
        return matchesSearch && impl.implementation_status === 'complete';
      case 'overdue':
        const targetDate = impl.target_go_live_date ? new Date(impl.target_go_live_date) : null;
        const isOverdue = targetDate && targetDate < new Date() && impl.implementation_status !== 'complete';
        return matchesSearch && isOverdue;
      default:
        return matchesSearch;
    }
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'planning': return 'default';
      case 'technical_setup': return 'secondary';
      case 'branding': return 'secondary';
      case 'training': return 'secondary';
      case 'testing': return 'secondary';
      case 'live': return 'default';
      case 'complete': return 'default';
      default: return 'secondary';
    }
  };

  const getTrainingStatusColor = (status: string) => {
    switch (status) {
      case 'not_started': return 'secondary';
      case 'scheduled': return 'default';
      case 'in_progress': return 'default';
      case 'completed': return 'default';
      default: return 'secondary';
    }
  };

  const getBrandingStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'secondary';
      case 'received': return 'default';
      case 'approved': return 'default';
      case 'implemented': return 'default';
      default: return 'secondary';
    }
  };

  const calculateProgress = (implementation: WhiteLabelImplementation) => {
    const stageIndex = implementationStages.findIndex(stage => stage.id === implementation.implementation_status);
    return ((stageIndex + 1) / implementationStages.length) * 100;
  };

  const formatCurrency = (amount?: number) => {
    return amount ? new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount) : 'TBD';
  };

  const formatDate = (dateString?: string) => {
    return dateString ? parseLocalDate(dateString).toLocaleDateString() : 'TBD';
  };

  const getDaysUntilGoLive = (targetDate?: string) => {
    if (!targetDate) return null;
    const target = new Date(targetDate);
    const now = new Date();
    const diffTime = target.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const ImplementationCard = ({ implementation }: { implementation: WhiteLabelImplementation }) => {
    const progress = calculateProgress(implementation);
    const daysUntilGoLive = getDaysUntilGoLive(implementation.target_go_live_date);
    const isOverdue = daysUntilGoLive !== null && daysUntilGoLive < 0;
    const currentStage = implementationStages.find(stage => stage.id === implementation.implementation_status);
    const StageIcon = currentStage?.icon || Clock;

    return (
      <Card className="mb-4 hover:shadow-md transition-shadow">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="font-semibold text-sm">
                {implementation.white_label_prospects?.organization_name || 'Unknown Organization'}
              </h3>
              <p className="text-xs text-muted-foreground">
                {implementation.white_label_prospects?.contact_name}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={getStatusColor(implementation.implementation_status)}>
                <StageIcon className="h-3 w-3 mr-1" />
                {currentStage?.name || implementation.implementation_status}
              </Badge>
              {isOverdue && (
                <Badge variant="destructive">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Overdue
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span>Implementation Progress</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Monthly Fee</p>
                <p className="font-medium">{formatCurrency(implementation.monthly_fee)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Go-Live Target</p>
                <p className="font-medium">{formatDate(implementation.target_go_live_date)}</p>
              </div>
            </div>
          </div>

          {/* Contract Details */}
          <div className="grid grid-cols-3 gap-4 text-xs">
            <div>
              <p className="text-muted-foreground">Contract Length</p>
              <p className="font-medium">{implementation.contract_length_months} months</p>
            </div>
            <div>
              <p className="text-muted-foreground">Setup Fee</p>
              <p className="font-medium">{formatCurrency(implementation.setup_fee)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Branding Package</p>
              <p className="font-medium capitalize">{implementation.branding_package}</p>
            </div>
          </div>

          {/* Status Details */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Staff Training</span>
                <Badge variant={getTrainingStatusColor(implementation.staff_training_status)} className="text-xs">
                  {implementation.staff_training_status.replace('_', ' ')}
                </Badge>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Branding Assets</span>
                <Badge variant={getBrandingStatusColor(implementation.branding_assets_status)} className="text-xs">
                  {implementation.branding_assets_status}
                </Badge>
              </div>
            </div>
          </div>

          {/* Custom Domain */}
          {implementation.custom_domain && (
            <div className="flex items-center gap-2 text-sm">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Domain:</span>
              <span className="text-xs font-mono">{implementation.custom_domain}</span>
            </div>
          )}

          {/* Days Until Go-Live */}
          {daysUntilGoLive !== null && (
            <div className={`flex items-center gap-2 text-sm ${isOverdue ? 'text-red-600' : 'text-green-600'}`}>
              <Clock className="h-4 w-4" />
              <span className="text-xs">
                {isOverdue 
                  ? `${Math.abs(daysUntilGoLive)} days overdue`
                  : `${daysUntilGoLive} days until go-live`
                }
              </span>
            </div>
          )}

          {/* Milestones */}
          {implementation.milestones && implementation.milestones.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Recent Milestones:</p>
              <div className="space-y-1">
                {implementation.milestones.slice(0, 2).map((milestone: any, index: number) => (
                  <div key={index} className="flex items-center gap-2 text-xs">
                    <CheckCircle2 className="h-3 w-3 text-green-600" />
                    <span>{milestone.name}</span>
                    <span className="text-muted-foreground">
                      {new Date(milestone.completed_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Implementation Notes */}
          {implementation.implementation_notes && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Notes:</p>
              <p className="text-xs bg-muted p-2 rounded">
                {implementation.implementation_notes.length > 100 
                  ? `${implementation.implementation_notes.substring(0, 100)}...` 
                  : implementation.implementation_notes
                }
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" size="sm">
              View Details
            </Button>
            <Button variant="outline" size="sm">
              Update Status
            </Button>
            {implementation.implementation_status === 'live' && (
              <Button size="sm">
                Monitor
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Implementation Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">
              {implementations.filter(i => !['complete'].includes(i.implementation_status)).length}
            </div>
            <div className="text-sm text-muted-foreground">Active Implementations</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">
              {implementations.filter(i => i.implementation_status === 'live').length}
            </div>
            <div className="text-sm text-muted-foreground">Live Implementations</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-red-600">
              {implementations.filter(i => {
                const targetDate = i.target_go_live_date ? new Date(i.target_go_live_date) : null;
                return targetDate && targetDate < new Date() && i.implementation_status !== 'complete';
              }).length}
            </div>
            <div className="text-sm text-muted-foreground">Overdue</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">
              {implementations.reduce((sum, i) => sum + (i.monthly_fee || 0), 0).toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0
              })}
            </div>
            <div className="text-sm text-muted-foreground">Monthly Revenue</div>
          </CardContent>
        </Card>
      </div>

      {/* Implementation Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Implementation Pipeline</CardTitle>
          <CardDescription>Track progress across all implementation stages</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-2">
            {implementationStages.map(stage => {
              const stageImplementations = implementations.filter(i => i.implementation_status === stage.id);
              const StageIcon = stage.icon;
              
              return (
                <Card key={stage.id} className={`${stage.color} border-0`}>
                  <CardContent className="p-3 text-center">
                    <StageIcon className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                    <div className="text-lg font-bold">{stageImplementations.length}</div>
                    <div className="text-xs text-muted-foreground">{stage.name}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Implementation List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Implementation Tracker</CardTitle>
          <CardDescription>Monitor and manage all white label implementations</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="active">Active</TabsTrigger>
              <TabsTrigger value="completed">Completed</TabsTrigger>
              <TabsTrigger value="overdue">Overdue</TabsTrigger>
              <TabsTrigger value="all">All</TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="space-y-4">
              {filteredImplementations.length === 0 ? (
                <div className="text-center py-8">
                  <Rocket className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">No implementations found</p>
                  <p className="text-sm text-muted-foreground">
                    {activeTab === 'active' && 'No active implementations'}
                    {activeTab === 'completed' && 'No completed implementations yet'}
                    {activeTab === 'overdue' && 'No overdue implementations'}
                    {activeTab === 'all' && 'Start your first implementation to get started'}
                  </p>
                </div>
              ) : (
                <div className="max-h-[600px] overflow-y-auto space-y-4">
                  {filteredImplementations.map(implementation => (
                    <ImplementationCard key={implementation.id} implementation={implementation} />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default WhiteLabelImplementationTracker;